/**
 * Project setup redesign — Track A (OAuth return + setup backend): namespace `oauthReturn`.
 *
 * Top-level namespaces owned by this track only. `oauthReturnNl` is typed against
 * `oauthReturnEn`, so a key missing in Dutch fails typecheck exactly as it does in
 * the main dictionary.
 *
 * `oauthReturn.reason.<code>` covers every `SetupReasonCode`
 * (`src/lib/project-setup/types.ts`): one plain sentence per failure, with the
 * next action named in it. The raw provider code (`detail`) is shown only
 * under `oauthReturn.technicalDetails`. `{provider}` is the provider's
 * recognisable name from `oauthReturn.provider.<id>`.
 */
export const oauthReturnEn = {
  oauthReturn: {
    provider: {
      gmail: "Google",
      office365: "Microsoft",
      slack: "Slack",
      github: "GitHub",
      gitlab: "GitLab",
      bitbucket: "Bitbucket",
      jira: "Jira",
      linear: "Linear",
    },
    ok: "{provider} is connected. You can continue where you left off.",
    errTitle: "The connection with {provider} was not completed",
    technicalDetails: "Technical details",
    tryAgain: "Try again",
    reconnect: "Sign in again",
    reviewPermissions: "Review permissions",
    reason: {
      consent_denied: "You did not give {provider} permission, so nothing was connected. You can try again whenever you like.",
      exchange_failed: "{provider} did not finish the sign-in. Nothing was connected. Try again.",
      state_invalid: "This sign-in link is not valid or was already used. Start the connection again from this page.",
      state_expired: "The sign-in took longer than 10 minutes and has expired. Start the connection again.",
      user_mismatch: "This sign-in was started by a different account or organization, so FixControl did not store anything. Start the connection again while signed in as yourself.",
      token_expired: "The connection with {provider} has expired or was revoked. Sign in again to restore it.",
      missing_permission: "{provider} connected your account, but FixControl does not yet have permission to read this mailbox. Review the permissions and try again.",
      mailbox_not_found: "FixControl could not open this mailbox. Check the address, or ask your administrator to give the signed-in account access to it.",
      address_mismatch: "This address does not belong to the account you signed in with. Choose another mailbox or sign in with the right account.",
      not_configured: "Signing in with {provider} is not set up for your organization yet. Ask your FixControl administrator to set it up.",
      already_linked_elsewhere: "This is already connected to another project. Disconnect it there first, or choose another one.",
      forbidden: "You do not have permission to do this in this project. Ask a project administrator.",
      connection_in_use: "This {provider} connection already reads another mailbox that is in use. Connect this mailbox with its own sign-in instead.",
      ingest_disabled: "Receiving mail is switched off for this mailbox. Turn it on to receive work in this project.",
      project_resolution_mismatch: "New mail to this address would not arrive in this project yet. Connect the mailbox to this project.",
      provider_unavailable: "{provider} could not be reached just now. Wait a moment and try again.",
      unknown: "Something went wrong while connecting {provider}. Try again.",
    },
  },
};

export const oauthReturnNl: typeof oauthReturnEn = {
  oauthReturn: {
    provider: {
      gmail: "Google",
      office365: "Microsoft",
      slack: "Slack",
      github: "GitHub",
      gitlab: "GitLab",
      bitbucket: "Bitbucket",
      jira: "Jira",
      linear: "Linear",
    },
    ok: "{provider} is verbonden. Je kunt verder waar je gebleven was.",
    errTitle: "De verbinding met {provider} is niet afgerond",
    technicalDetails: "Technische details",
    tryAgain: "Opnieuw proberen",
    reconnect: "Opnieuw verbinden",
    reviewPermissions: "Toestemming bekijken",
    reason: {
      consent_denied: "Je hebt {provider} geen toestemming gegeven, dus er is niets verbonden. Je kunt het altijd opnieuw proberen.",
      exchange_failed: "{provider} heeft het inloggen niet afgerond. Er is niets verbonden. Probeer het opnieuw.",
      state_invalid: "Deze inloglink is niet geldig of is al gebruikt. Start de verbinding opnieuw vanaf deze pagina.",
      state_expired: "Het inloggen duurde langer dan 10 minuten en is verlopen. Start de verbinding opnieuw.",
      user_mismatch: "Dit inloggen is gestart door een ander account of een andere organisatie, dus FixControl heeft niets opgeslagen. Start de verbinding opnieuw terwijl je met je eigen account bent ingelogd.",
      token_expired: "De verbinding met {provider} is verlopen of ingetrokken. Log opnieuw in om hem te herstellen.",
      missing_permission: "{provider} heeft je account verbonden, maar FixControl heeft nog geen toestemming om deze mailbox te lezen. Bekijk de toestemming en probeer het opnieuw.",
      mailbox_not_found: "FixControl kan deze mailbox niet openen. Controleer het adres, of vraag je beheerder om het ingelogde account toegang te geven.",
      address_mismatch: "Dit adres hoort niet bij het account waarmee je bent ingelogd. Kies een andere mailbox of log in met het juiste account.",
      not_configured: "Inloggen met {provider} is voor je organisatie nog niet ingesteld. Vraag je FixControl-beheerder om dit in te stellen.",
      already_linked_elsewhere: "Dit is al gekoppeld aan een ander project. Ontkoppel het daar eerst, of kies een andere.",
      forbidden: "Je hebt geen toestemming om dit in dit project te doen. Vraag het aan een projectbeheerder.",
      connection_in_use: "Deze {provider}-verbinding leest al een andere mailbox die in gebruik is. Koppel deze mailbox met een eigen inlog.",
      ingest_disabled: "Mail ontvangen staat uit voor deze mailbox. Zet het aan om werk in dit project te ontvangen.",
      project_resolution_mismatch: "Nieuwe mail naar dit adres komt nog niet in dit project terecht. Koppel de mailbox aan dit project.",
      provider_unavailable: "{provider} is op dit moment niet bereikbaar. Wacht even en probeer het opnieuw.",
      unknown: "Er ging iets mis bij het verbinden met {provider}. Probeer het opnieuw.",
    },
  },
};
